// Admin integrations (MUI, /admin/integrations). Presentation port of
// dashboard/admin/IntegrationsPage — same data (getAdminIntegrations).
import { useEffect, useState } from "react";
import {
  Box,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from "@mui/material";
import { getAdminIntegrations } from "../../lib/brain-api";
import type { IntegrationStatus } from "../../lib/brain-types";
import { brainTokens } from "../../design/brainTheme";
import { PageHeader, StatusPill, EmptyState } from "../../mui";

const c = brainTokens.color;

export function AdminIntegrationsPageMui() {
  const [items, setItems] = useState<IntegrationStatus[] | null>(null);
  const [err, setErr] = useState(false);
  useEffect(() => {
    getAdminIntegrations()
      .then((r) => setItems(r.integrations))
      .catch(() => setErr(true));
  }, []);

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
      <PageHeader
        title="Integrations"
        eyebrow="Read-only — integrations are configured at deploy time; editing comes in a later phase."
      />
      {err && (
        <Box sx={{ fontSize: 13, color: c.dangerText, bgcolor: c.dangerBg, border: `1px solid ${c.dangerBorder}`, borderRadius: `${brainTokens.radius.panel}px`, px: 2, py: 1.25 }}>
          Couldn&apos;t load integrations.
        </Box>
      )}
      {!err && !items && (
        <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
          <CircularProgress size={28} sx={{ color: c.teal }} />
        </Box>
      )}
      {!err && items && (
        items.length === 0 ? (
          <EmptyState title="No integrations configured." />
        ) : (
          <TableContainer sx={{ border: `1px solid ${c.border}`, borderRadius: `${brainTokens.radius.panel}px`, overflow: "hidden" }}>
            <Table>
              <TableHead>
                <TableRow sx={{ bgcolor: c.panelBg }}>
                  <TableCell sx={{ fontWeight: 600, color: c.ink3 }}>Integration</TableCell>
                  <TableCell sx={{ fontWeight: 600, color: c.ink3 }}>Status</TableCell>
                  <TableCell sx={{ fontWeight: 600, color: c.ink3 }}>Last run</TableCell>
                  <TableCell sx={{ fontWeight: 600, color: c.ink3 }}>Result</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {items.map((i) => (
                  <TableRow key={i.key} sx={{ "& + &": { borderTop: `1px solid ${c.borderLight}` } }}>
                    <TableCell>{i.label}</TableCell>
                    <TableCell>
                      <Box sx={{ display: "inline-flex", alignItems: "center", gap: "7px", flexWrap: "wrap" }}>
                        <StatusPill tone={i.on ? "success" : "neutral"} label={i.on ? "On" : "Off"} dot />
                        {!i.configured && (
                          <Box component="span" sx={{ fontSize: 12, color: c.muted2, ml: 0.5 }}>
                            (not configured)
                          </Box>
                        )}
                      </Box>
                    </TableCell>
                    <TableCell sx={{ color: c.muted2, fontSize: 13 }}>
                      {i.lastRun ? new Date(i.lastRun).toLocaleString() : "—"}
                    </TableCell>
                    <TableCell sx={{ color: c.muted2, fontSize: 13, fontFamily: brainTokens.font.mono }}>
                      {Object.keys(i.counts).length
                        ? Object.entries(i.counts).map(([k, v]) => `${k}:${v}`).join("  ")
                        : "—"}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )
      )}
    </Box>
  );
}
